import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useLoader } from "../../../../context/LoaderProvider.jsx";

// Import Bootstrap CSS (make sure Bootstrap is installed in your project)
// If not installed: npm install bootstrap
import "bootstrap/dist/css/bootstrap.min.css";

const SuperadminDashboard = () => {
  const navigate = useNavigate();
  const { showLoader, hideLoader } = useLoader();
  const [stats, setStats] = useState({
    totalVendors: 0,
    totalOrders: 0,
    totalRevenue: 0,
    pendingSettlements: 0,
    activeAdvertisements: 0,
    todayRevenue: 0
  });
  const [recentOrders, setRecentOrders] = useState([]);
  const [topVendors, setTopVendors] = useState([]);
  const [revenueData, setRevenueData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");

  // Internal CSS styles
  const styles = {
    dashboard: {
      backgroundColor: "#f8f9fa",
      minHeight: "100vh"
    },
    header: {
      backgroundColor: "#ffffff",
      borderRadius: "10px",
      padding: "20px",
      marginBottom: "20px",
      boxShadow: "0 2px 4px rgba(0,0,0,0.1)"
    },
    statCard: {
      borderRadius: "10px",
      padding: "15px",
      marginBottom: "15px",
      boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
      transition: "transform 0.3s, box-shadow 0.3s"
    },
    statCardHover: {
      transform: "translateY(-5px)",
      boxShadow: "0 4px 8px rgba(0,0,0,0.2)"
    },
    iconContainer: {
      width: "50px",
      height: "50px",
      borderRadius: "10px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: "24px",
      marginBottom: "10px"
    },
    chartContainer: {
      backgroundColor: "#ffffff",
      borderRadius: "10px",
      padding: "20px",
      marginBottom: "20px",
      boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
      height: "350px"
    },
    tableCard: {
      backgroundColor: "#ffffff",
      borderRadius: "10px",
      padding: "20px",
      marginBottom: "20px",
      boxShadow: "0 2px 4px rgba(0,0,0,0.1)"
    },
    quickActionButton: {
      borderRadius: "8px",
      padding: "10px 15px",
      margin: "5px",
      fontWeight: "500",
      display: "flex",
      alignItems: "center",
      gap: "8px",
      transition: "all 0.3s"
    },
    statusBadge: {
      padding: "4px 12px",
      borderRadius: "20px",
      fontSize: "12px",
      fontWeight: "500"
    },
    loadingSpinner: {
      width: "50px",
      height: "50px",
      border: "4px solid #f3f3f3",
      borderTop: "4px solid #b15cd6",
      borderRadius: "50%",
      animation: "spin 1s linear infinite"
    }
  };

  // Fetch dashboard data
  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      showLoader();
      const token = localStorage.getItem("authToken");

      // Fetch statistics
      const vendorsRes = await axios.get("https://backend.maamaas.com/food/api/vendors/get/all", {
        headers: { Authorization: `Bearer ${token}` }
      });

      const ordersRes = await axios.get("https://backend.maamaas.com/food/api/orders/get/all", {
        headers: { Authorization: `Bearer ${token}` }
      });

      const settlementsRes = await axios.get("https://backend.maamaas.com/food/api/settlements/get/all", {
        headers: { Authorization: `Bearer ${token}` }
      });

      const advertisementsRes = await axios.get("https://backend.maamaas.com/food/api/advertisements/valid", {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Calculate statistics
      const totalVendors = vendorsRes.data.length || 0;
      const totalOrders = ordersRes.data.length || 0;
      const totalRevenue = 1250000; // Mock data - replace with actual API
      const todayRevenue = 45000; // Mock data

      const settlements = settlementsRes.data.settlements || settlementsRes.data;
      const pendingSettlements = Array.isArray(settlements)
        ? settlements.filter(s => s.paymentStatus === "PENDING").length
        : 0;

      const activeAdvertisements = advertisementsRes.data.length || 0;

      setStats({
        totalVendors,
        totalOrders,
        totalRevenue,
        pendingSettlements,
        activeAdvertisements,
        todayRevenue
      });

      // Mock data for charts and tables (replace with actual API calls)
      setRecentOrders([
        { id: "#ORD001", vendor: "Restaurant A", amount: 1250, status: "Delivered", date: "2024-01-07" },
        { id: "#ORD002", vendor: "Restaurant B", amount: 850, status: "Processing", date: "2024-01-07" },
        { id: "#ORD003", vendor: "Restaurant C", amount: 2100, status: "Delivered", date: "2024-01-06" },
        { id: "#ORD004", vendor: "Restaurant D", amount: 950, status: "Pending", date: "2024-01-06" },
        { id: "#ORD005", vendor: "Restaurant E", amount: 1750, status: "Delivered", date: "2024-01-05" }
      ]);

      setTopVendors([
        { id: 1, name: "Restaurant A", orders: 245, revenue: 125000, rating: 4.8 },
        { id: 2, name: "Restaurant B", orders: 189, revenue: 98000, rating: 4.7 },
        { id: 3, name: "Restaurant C", orders: 167, revenue: 85000, rating: 4.6 },
        { id: 4, name: "Restaurant D", orders: 145, revenue: 72000, rating: 4.5 },
        { id: 5, name: "Restaurant E", orders: 128, revenue: 65000, rating: 4.4 }
      ]);

      // Mock revenue data for chart
      setRevenueData([
        { day: "Mon", revenue: 25000 },
        { day: "Tue", revenue: 32000 },
        { day: "Wed", revenue: 28000 },
        { day: "Thu", revenue: 40000 },
        { day: "Fri", revenue: 52000 },
        { day: "Sat", revenue: 48000 },
        { day: "Sun", revenue: 35000 }
      ]);

    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    } finally {
      setLoading(false);
      hideLoader();
    }
  };

  // Navigation handlers
  const navigateToVendors = () => navigate("/superadmin/vendors");
  const navigateToOrders = () => navigate("/superadmin/orders");
  const navigateToSettlements = () => navigate("/superadmin/settlements");
  const navigateToAdvertisements = () => navigate("/superadmin/advertisements");

  // Get status badge color
  const getStatusColor = (status) => {
    switch (status.toLowerCase()) {
      case 'delivered': return 'success';
      case 'processing': return 'primary';
      case 'pending': return 'warning';
      default: return 'secondary';
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: "60vh" }}>
        <div style={styles.loadingSpinner}></div>
      </div>
    );
  }

  return (

    <div style={styles.dashboard}>
      <div className="container-fluid py-4">
        {/* Header */}
        <div style={styles.header}>
          <div className="row align-items-center">
            <div className="col-md-8">
              <h1 className="h2 mb-2">Superadmin Dashboard</h1>
              <p className="text-muted mb-0">
                Welcome back! Here's an overview of your platform performance.
              </p>
            </div>
            <div className="col-md-4 text-end">
              <div className="btn-group" role="group">
                <button
                  className={`btn ${activeTab === "overview" ? "btn-primary" : "btn-outline-primary"}`}
                  onClick={() => setActiveTab("overview")}
                >
                  Overview
                </button>
                <button
                  className={`btn ${activeTab === "analytics" ? "btn-primary" : "btn-outline-primary"}`}
                  onClick={() => setActiveTab("analytics")}
                >
                  Analytics
                </button>
                <button
                  className={`btn ${activeTab === "reports" ? "btn-primary" : "btn-outline-primary"}`}
                  onClick={() => setActiveTab("reports")}
                >
                  Reports
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="row mb-4">
          <div className="col-xl-2 col-md-4 col-sm-6 mb-3">
            <div
              className="card border-0 h-100"
              style={styles.statCard}
              onMouseEnter={(e) => e.currentTarget.style.transform = "translateY(-5px)"}
              onMouseLeave={(e) => e.currentTarget.style.transform = "translateY(0)"}
              onClick={navigateToVendors}
            >
              <div
                className="card-body text-center"
                style={{ cursor: "pointer" }}
              >
                <div style={{ ...styles.iconContainer, backgroundColor: "#e3f2fd" }}>
                  <span style={{ color: "#2196f3" }}>🏪</span>
                </div>
                <h3 className="card-title mb-1">{stats.totalVendors}</h3>
                <p className="card-text text-muted mb-0">Total Vendors</p>
                <small className="text-success">
                  <i className="bi bi-arrow-up"></i> 12% from last month
                </small>
              </div>
            </div>
          </div>

          <div className="col-xl-2 col-md-4 col-sm-6 mb-3">
            <div
              className="card border-0 h-100"
              style={styles.statCard}
              onMouseEnter={(e) => e.currentTarget.style.transform = "translateY(-5px)"}
              onMouseLeave={(e) => e.currentTarget.style.transform = "translateY(0)"}
              onClick={navigateToOrders}
            >
              <div
                className="card-body text-center"
                style={{ cursor: "pointer" }}
              >
                <div style={{ ...styles.iconContainer, backgroundColor: "#e8f5e9" }}>
                  <span style={{ color: "#4caf50" }}>📦</span>
                </div>
                <h3 className="card-title mb-1">{stats.totalOrders}</h3>
                <p className="card-text text-muted mb-0">Total Orders</p>
                <small className="text-success">
                  <i className="bi bi-arrow-up"></i> 8% from yesterday
                </small>
              </div>
            </div>
          </div>

          <div className="col-xl-2 col-md-4 col-sm-6 mb-3">
            <div
              className="card border-0 h-100"
              style={styles.statCard}
              onMouseEnter={(e) => e.currentTarget.style.transform = "translateY(-5px)"}
              onMouseLeave={(e) => e.currentTarget.style.transform = "translateY(0)"}
            >
              <div className="card-body text-center">
                <div style={{ ...styles.iconContainer, backgroundColor: "#fff3e0" }}>
                  <span style={{ color: "#ff9800" }}>💰</span>
                </div>
                <h3 className="card-title mb-1">₹{stats.todayRevenue.toLocaleString()}</h3>
                <p className="card-text text-muted mb-0">Today's Revenue</p>
                <small className="text-success">
                  <i className="bi bi-arrow-up"></i> 15% from yesterday
                </small>
              </div>
            </div>
          </div>

          <div className="col-xl-2 col-md-4 col-sm-6 mb-3">
            <div
              className="card border-0 h-100"
              style={styles.statCard}
              onMouseEnter={(e) => e.currentTarget.style.transform = "translateY(-5px)"}
              onMouseLeave={(e) => e.currentTarget.style.transform = "translateY(0)"}
              onClick={navigateToSettlements}
            >
              <div
                className="card-body text-center"
                style={{ cursor: "pointer" }}
              >
                <div style={{ ...styles.iconContainer, backgroundColor: "#ffebee" }}>
                  <span style={{ color: "#f44336" }}>⚖️</span>
                </div>
                <h3 className="card-title mb-1">{stats.pendingSettlements}</h3>
                <p className="card-text text-muted mb-0">Pending Settlements</p>
                <small className="text-warning">
                  <i className="bi bi-clock"></i> Needs attention
                </small>
              </div>
            </div>
          </div>

          <div className="col-xl-2 col-md-4 col-sm-6 mb-3">
            <div
              className="card border-0 h-100"
              style={styles.statCard}
              onMouseEnter={(e) => e.currentTarget.style.transform = "translateY(-5px)"}
              onMouseLeave={(e) => e.currentTarget.style.transform = "translateY(0)"}
              onClick={navigateToAdvertisements}
            >
              <div
                className="card-body text-center"
                style={{ cursor: "pointer" }}
              >
                <div style={{ ...styles.iconContainer, backgroundColor: "#f3e5f5" }}>
                  <span style={{ color: "#9c27b0" }}>📢</span>
                </div>
                <h3 className="card-title mb-1">{stats.activeAdvertisements}</h3>
                <p className="card-text text-muted mb-0">Active Ads</p>
                <small className="text-info">
                  <i className="bi bi-eye"></i> Running campaigns
                </small>
              </div>
            </div>
          </div>

          <div className="col-xl-2 col-md-4 col-sm-6 mb-3">
            <div
              className="card border-0 h-100"
              style={styles.statCard}
              onMouseEnter={(e) => e.currentTarget.style.transform = "translateY(-5px)"}
              onMouseLeave={(e) => e.currentTarget.style.transform = "translateY(0)"}
            >
              <div className="card-body text-center">
                <div style={{ ...styles.iconContainer, backgroundColor: "#e0f2f1" }}>
                  <span style={{ color: "#009688" }}>📊</span>
                </div>
                <h3 className="card-title mb-1">₹{stats.totalRevenue.toLocaleString()}</h3>
                <p className="card-text text-muted mb-0">Total Revenue</p>
                <small className="text-success">
                  <i className="bi bi-graph-up"></i> 25% growth
                </small>
              </div>
            </div>
          </div>
        </div>

        {/* Charts and Tables Row */}
        <div className="row mb-4">
          {/* Revenue Chart */}
          <div className="col-lg-8 mb-4">
            <div style={styles.chartContainer}>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="mb-0">Weekly Revenue Trend</h5>
                <select className="form-select form-select-sm" style={{ width: "auto" }}>
                  <option>This Week</option>
                  <option>Last Week</option>
                  <option>This Month</option>
                </select>
              </div>
              <div className="chart-wrapper" style={{ height: "280px" }}>
                {/* Simple bar chart using Bootstrap progress bars */}
                <div className="d-flex align-items-end h-100" style={{ gap: "10px" }}>
                  {revenueData.map((item, index) => (
                    <div key={index} className="text-center" style={{ flex: 1 }}>
                      <div className="mb-1" style={{ fontSize: "12px", color: "#666" }}>{item.day}</div>
                      <div
                        className="progress"
                        style={{
                          height: `${(item.revenue / 60000) * 100}%`,
                          minHeight: "20px",
                          backgroundColor: "#4caf50",
                          borderRadius: "5px"
                        }}
                      >
                        <div
                          className="progress-bar bg-success"
                          role="progressbar"
                          style={{ width: "100%" }}
                        ></div>
                      </div>
                      <div className="mt-1" style={{ fontSize: "12px", fontWeight: "500" }}>
                        ₹{item.revenue.toLocaleString()}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Quick Stats */}
          <div className="col-lg-4 mb-4">
            <div style={styles.tableCard}>
              <h5 className="mb-3">Platform Overview</h5>
              <div className="list-group list-group-flush">
                <div className="list-group-item d-flex justify-content-between align-items-center border-0 px-0">
                  <div>
                    <i className="bi bi-check-circle text-success me-2"></i>
                    <span>Active Vendors</span>
                  </div>
                  <span className="badge bg-success rounded-pill">{Math.floor(stats.totalVendors * 0.85)}</span>
                </div>
                <div className="list-group-item d-flex justify-content-between align-items-center border-0 px-0">
                  <div>
                    <i className="bi bi-clock text-warning me-2"></i>
                    <span>Avg Order Value</span>
                  </div>
                  <span className="badge bg-warning rounded-pill">₹450</span>
                </div>
                <div className="list-group-item d-flex justify-content-between align-items-center border-0 px-0">
                  <div>
                    <i className="bi bi-people text-primary me-2"></i>
                    <span>Active Customers</span>
                  </div>
                  <span className="badge bg-primary rounded-pill">1,250</span>
                </div>
                <div className="list-group-item d-flex justify-content-between align-items-center border-0 px-0">
                  <div>
                    <i className="bi bi-star text-info me-2"></i>
                    <span>Platform Rating</span>
                  </div>
                  <span className="badge bg-info rounded-pill">4.7/5</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Tables Row */}
        <div className="row">
          {/* Recent Orders Table */}
          <div className="col-lg-6 mb-4">
            <div style={styles.tableCard}>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="mb-0">Recent Orders</h5>
                <button
                  className="btn btn-sm btn-outline-primary"
                  onClick={navigateToOrders}
                >
                  View All
                </button>
              </div>
              <div className="table-responsive">
                <table className="table table-hover">
                  <thead>
                    <tr>
                      <th>Order ID</th>
                      <th>Vendor</th>
                      <th>Amount</th>
                      <th>Status</th>
                      <th>Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentOrders.map((order, index) => (
                      <tr key={index} style={{ cursor: "pointer" }}>
                        <td>{order.id}</td>
                        <td>{order.vendor}</td>
                        <td>₹{order.amount}</td>
                        <td>
                          <span
                            className={`badge bg-${getStatusColor(order.status)}`}
                            style={styles.statusBadge}
                          >
                            {order.status}
                          </span>
                        </td>
                        <td>{order.date}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Top Vendors Table */}
          <div className="col-lg-6 mb-4">
            <div style={styles.tableCard}>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="mb-0">Top Performing Vendors</h5>
                <button
                  className="btn btn-sm btn-outline-primary"
                  onClick={navigateToVendors}
                >
                  View All
                </button>
              </div>
              <div className="table-responsive">
                <table className="table table-hover">
                  <thead>
                    <tr>
                      <th>Vendor ID</th>
                      <th>Name</th>
                      <th>Orders</th>
                      <th>Revenue</th>
                      <th>Rating</th>
                    </tr>
                  </thead>
                  <tbody>
                    {topVendors.map((vendor, index) => (
                      <tr key={index} style={{ cursor: "pointer" }}>
                        <td>#{vendor.id}</td>
                        <td>
                          <div className="d-flex align-items-center">
                            <div
                              className="rounded-circle me-2"
                              style={{
                                width: "30px",
                                height: "30px",
                                backgroundColor: "#e3f2fd",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: "14px"
                              }}
                            >
                              🏪
                            </div>
                            {vendor.name}
                          </div>
                        </td>
                        <td>{vendor.orders}</td>
                        <td>₹{vendor.revenue.toLocaleString()}</td>
                        <td>
                          <span className="badge bg-warning text-dark">
                            <i className="bi bi-star-fill me-1"></i>
                            {vendor.rating}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="row">
          <div className="col-12">
            <div style={styles.tableCard}>
              <h5 className="mb-3">Quick Actions</h5>
              <div className="d-flex flex-wrap">
                <button
                  className="btn btn-primary"
                  style={styles.quickActionButton}
                  onClick={() => navigate("/superadmin/add-vendor")}
                >
                  <i className="bi bi-plus-circle"></i>
                  Add New Vendor
                </button>
                <button
                  className="btn btn-success"
                  style={styles.quickActionButton}
                  onClick={() => navigate("/superadmin/create-advertisement")}
                >
                  <i className="bi bi-megaphone"></i>
                  Create Advertisement
                </button>
                <button
                  className="btn btn-warning"
                  style={styles.quickActionButton}
                  onClick={navigateToSettlements}
                >
                  <i className="bi bi-cash-coin"></i>
                  Process Settlements
                </button>
                <button
                  className="btn btn-info"
                  style={styles.quickActionButton}
                  onClick={() => navigate("/superadmin/reports")}
                >
                  <i className="bi bi-file-earmark-text"></i>
                  Generate Reports
                </button>
                <button
                  className="btn btn-secondary"
                  style={styles.quickActionButton}
                  onClick={() => navigate("/superadmin/settings")}
                >
                  <i className="bi bi-gear"></i>
                  Settings
                </button>
                <button
                  className="btn btn-dark"
                  style={styles.quickActionButton}
                  onClick={fetchDashboardData}
                >
                  <i className="bi bi-arrow-clockwise"></i>
                  Refresh Dashboard
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Footer Stats */}
        <div className="row mt-4">
          <div className="col-12">
            <div className="card border-0" style={{ backgroundColor: "transparent" }}>
              <div className="card-body p-0">
                <div className="row text-center">
                  <div className="col-md-3 col-6 mb-3">
                    <div className="p-3 bg-white rounded" style={{ boxShadow: "0 2px 4px rgba(0,0,0,0.1)" }}>
                      <h6 className="text-muted mb-1">Commission Earned</h6>
                      <h4 className="mb-0">₹1,25,000</h4>
                      <small className="text-success">
                        <i className="bi bi-arrow-up"></i> 18% this month
                      </small>
                    </div>
                  </div>
                  <div className="col-md-3 col-6 mb-3">
                    <div className="p-3 bg-white rounded" style={{ boxShadow: "0 2px 4px rgba(0,0,0,0.1)" }}>
                      <h6 className="text-muted mb-1">Avg. Commission</h6>
                      <h4 className="mb-0">12.5%</h4>
                      <small className="text-info">Per transaction</small>
                    </div>
                  </div>
                  <div className="col-md-3 col-6 mb-3">
                    <div className="p-3 bg-white rounded" style={{ boxShadow: "0 2px 4px rgba(0,0,0,0.1)" }}>
                      <h6 className="text-muted mb-1">Settlements Processed</h6>
                      <h4 className="mb-0">48</h4>
                      <small className="text-success">This week</small>
                    </div>
                  </div>
                  <div className="col-md-3 col-6 mb-3">
                    <div className="p-3 bg-white rounded" style={{ boxShadow: "0 2px 4px rgba(0,0,0,0.1)" }}>
                      <h6 className="text-muted mb-1">Customer Satisfaction</h6>
                      <h4 className="mb-0">94%</h4>
                      <small className="text-success">
                        <i className="bi bi-emoji-smile"></i> Very Good
                      </small>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Add spinner animation */}
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        .stat-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 4px 8px rgba(0,0,0,0.2);
        }
        
        .quick-action-btn:hover {
          transform: scale(1.05);
        }
      `}</style>
    </div>
  );
};

export default SuperadminDashboard;